import {
    DEFAULT_DELIVERY_DATE_RANGE_DAYS,
    MAX_DELIVERY_DATE_RANGE_DAYS,
    formatDateIsoLocal,
    validateDeliveryDateRange,
} from '../../utils/deliveryDateRange';

/**
 * Utilitários do modal genérico de filtros de relatório.
 * O catálogo (chumbado hoje, parametrizado depois) descreve os campos;
 * estas funções interpretam esse schema sem conhecer cada relatório.
 */

const NUMERIC_FILTER_TYPES = new Set(['number', 'customer', 'driver', 'costCenter']);

/**
 * Período pré-selecionado dos relatórios.
 * Mantém os últimos 30 dias, mas encurta a data inicial quando o limite consultado é menor.
 * @param {number} [maxDays]
 * @param {Date} [now]
 * @returns {{ startDate: string, endDate: string }}
 */
export function getDefaultReportPeriod(maxDays, now = new Date()) {
    const end = new Date(now.getTime());
    const limit = Number.isInteger(maxDays) && maxDays > 0
        ? maxDays
        : MAX_DELIVERY_DATE_RANGE_DAYS;
    const span = Math.min(DEFAULT_DELIVERY_DATE_RANGE_DAYS, limit);
    const start = new Date(end.getTime());
    start.setDate(start.getDate() - span);
    return {
        startDate: formatDateIsoLocal(start),
        endDate: formatDateIsoLocal(end),
    };
}

export function isEmptyFilterValue(value) {
    if (value == null) return true;
    if (typeof value === 'string') return value.trim() === '';
    if (Array.isArray(value)) return value.length === 0;
    return false;
}

/**
 * Monta o estado inicial do formulário a partir do schema + valores opcionais.
 * @param {Array<{ name: string, type?: string, defaultValue?: unknown, displayNameKey?: string }>} filters
 * @param {Record<string, unknown>} [initialValues]
 */
export function getDefaultFilterValues(filters = [], initialValues = {}) {
    const defaults = {};

    filters.forEach((filter) => {
        if (!filter?.name) return;
        if (filter.displayNameKey) {
            defaults[filter.displayNameKey] = '';
        }
        if (filter.defaultValue !== undefined) {
            defaults[filter.name] = filter.defaultValue;
            return;
        }
        if (filter.type === 'checkbox') {
            defaults[filter.name] = false;
            return;
        }
        if (filter.type === 'multiselect') {
            defaults[filter.name] = [];
            return;
        }
        defaults[filter.name] = '';
    });

    return { ...defaults, ...initialValues };
}

/**
 * Aplica as regras de role do schema: resolve `required` (`requiredForRoles`) e
 * remove os campos que não valem para o usuário (`hiddenForRoles`).
 * `requiredForRoles` tem precedência: quem precisa informar o campo sempre o vê.
 *
 * @param {Array<{ name: string, required?: boolean, requiredForRoles?: string[], hiddenForRoles?: string[] }>} filters
 * @param {string[]} userRoles
 */
export function resolveFiltersForRoles(filters = [], userRoles = []) {
    const hasAnyRole = (roles) =>
        Array.isArray(roles) && roles.some((role) => userRoles.includes(role));

    const resolved = [];
    filters.forEach((filter) => {
        if (!filter?.name) return;
        const { requiredForRoles, hiddenForRoles, ...rest } = filter;
        const required = requiredForRoles
            ? hasAnyRole(requiredForRoles)
            : Boolean(filter.required);
        if (!required && hasAnyRole(hiddenForRoles)) return;
        resolved.push({ ...rest, required });
    });

    return resolved;
}

/**
 * Restaura os filtros que dependem (`dependsOn`) de um campo que acabou de mudar.
 * Ex.: trocar o cliente invalida o centro de custo já escolhido.
 *
 * @param {Record<string, unknown>} values
 * @param {string[]} changedNames
 * @param {Array<{ name: string, dependsOn?: string }>} filters
 */
export function clearDependentValues(values = {}, changedNames = [], filters = []) {
    const changed = new Set(changedNames);
    const dependents = filters.filter(
        (filter) => filter?.dependsOn && changed.has(filter.dependsOn)
    );
    if (dependents.length === 0) return values;

    return { ...values, ...getDefaultFilterValues(dependents) };
}

/**
 * Valida campos obrigatórios, IDs numéricos e intervalo de datas.
 * `maxDays` substitui o limite padrão quando a tela já consultou o parâmetro do sistema.
 * @param {Array} filters
 * @param {Record<string, unknown>} values
 * @param {{ maxDays?: number }} [options]
 * @returns {{ valid: boolean, message?: string, field?: string }}
 */
export function validateFilterValues(filters = [], values = {}, options = {}) {
    for (const filter of filters) {
        if (!filter?.name || !filter.required) continue;
        if (isEmptyFilterValue(values[filter.name])) {
            return {
                valid: false,
                field: filter.name,
                message: `Informe ${String(filter.label || filter.name).toLowerCase()}.`,
            };
        }
    }

    for (const filter of filters) {
        if (!filter?.name || !NUMERIC_FILTER_TYPES.has(filter.type)) continue;
        const raw = values[filter.name];
        if (isEmptyFilterValue(raw)) continue;
        const number = Number(raw);
        if (!Number.isFinite(number) || number <= 0 || !Number.isInteger(number)) {
            return {
                valid: false,
                field: filter.name,
                message: `Informe um ${String(filter.label || filter.name).toLowerCase()} válido.`,
            };
        }
    }

    const startDate = values.startDate;
    const endDate = values.endDate;
    if (startDate || endDate) {
        const range = validateDeliveryDateRange(startDate, endDate, options.maxDays);
        if (!range.valid) {
            return {
                valid: false,
                field: 'endDate',
                message: range.message,
            };
        }
    }

    return { valid: true };
}

/**
 * Troca a dica da data final pelo limite retornado em REPORT_MAX_PERIOD_DAYS.
 * Sem um inteiro positivo, devolve o mesmo array.
 * @param {Array<{ name?: string, hint?: string }>} filters
 * @param {number | null | undefined} maxDays
 */
export function applyReportPeriodLimitHint(filters = [], maxDays) {
    if (!Number.isInteger(maxDays) || maxDays < 1) return filters;
    const hint = `Intervalo máximo de ${maxDays} dias em relação à data inicial.`;
    return filters.map((filter) => (
        filter?.name === 'endDate' ? { ...filter, hint } : filter
    ));
}

/**
 * Monta `parameters` da API apenas com campos do schema (ignora displayName etc.).
 * Campos numéricos / customer / driver são convertidos para Number.
 * Multiselect com `emptyAsAllOptions` e lista vazia ("Todos") envia todos os `options`.
 *
 * @param {Record<string, unknown>} values
 * @param {{ exclude?: string[], filters?: Array<{ name: string, type?: string, options?: Array<{ value: string }>, emptyAsAllOptions?: boolean }> }} [options]
 */
export function buildReportParameters(values = {}, options = {}) {
    const exclude = new Set(options.exclude || ['format']);
    const filters = options.filters || [];
    const filterByName = new Map(filters.map((filter) => [filter.name, filter]));
    const allowedNames = filters.length > 0
        ? new Set(filters.map((filter) => filter.name).filter((name) => !exclude.has(name)))
        : null;
    const parameters = {};

    Object.entries(values).forEach(([key, value]) => {
        if (exclude.has(key)) return;
        if (allowedNames && !allowedNames.has(key)) return;

        const filter = filterByName.get(key);

        if (
            filter?.type === 'multiselect' &&
            filter.emptyAsAllOptions &&
            Array.isArray(value) &&
            value.length === 0 &&
            Array.isArray(filter.options) &&
            filter.options.length > 0
        ) {
            parameters[key] = filter.options.map((option) => option.value);
            return;
        }

        if (isEmptyFilterValue(value)) return;

        let next = typeof value === 'string' ? value.trim() : value;

        if (NUMERIC_FILTER_TYPES.has(filter?.type)) {
            const number = Number(next);
            if (!Number.isFinite(number)) return;
            next = number;
        }

        parameters[key] = next;
    });

    return parameters;
}
